import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Thermometer, AlertCircle, Copy, RotateCcw } from 'lucide-react';
import {
  DEFAULT_INPUTS,
  UNITS,
  FIELD_SPECS,
  ABSOLUTE_ZERO,
  PRECISION_OPTIONS,
  DEFAULT_PRECISION,
  validateField,
} from '../../lib/temperatureTools';
import { temperatureToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;

const VALUE_SPEC = FIELD_SPECS[0];

const TemperatureConverter = () => {
  const [unit, setUnit] = useState(DEFAULT_INPUTS.unit);
  const [rawValue, setRawValue] = useState(String(DEFAULT_INPUTS.value));
  const [precision, setPrecision] = useState(DEFAULT_PRECISION);

  const unitInfo = UNITS.find((u) => u.value === unit) || UNITS[0];
  const floor = ABSOLUTE_ZERO[unit];
  const fieldError = validateField(VALUE_SPEC, rawValue, unit);

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (fieldError) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      temperatureToolApi.convert({ value: Number(rawValue), unit })
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rawValue, unit, fieldError]);

  const formatted = useMemo(() => {
    if (!results) return null;
    return UNITS.map((u) => ({
      ...u,
      display: results[u.value].toFixed(precision),
    }));
  }, [results, precision]);

  const handleReset = () => {
    setUnit(DEFAULT_INPUTS.unit);
    setRawValue(String(DEFAULT_INPUTS.value));
    setPrecision(DEFAULT_PRECISION);
  };

  const handleCopy = async () => {
    if (!formatted) return;
    const text = formatted.map((u) => `${u.name}: ${u.display} ${u.label}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" aria-label="Celsius to Kelvin Calculator">
      <Card className="technical-card lg:col-span-2">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Thermometer className="h-5 w-5 text-autoxium-blue" />
            Temperature Calculator
          </CardTitle>
          <CardDescription>
            Convert temperature between 3 common units. Results update live as you type.
            Values below absolute zero are rejected.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Temperature
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="temperature-value" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Value <span className="normal-case text-muted-foreground/70">({unitInfo.label})</span>
                </Label>
                <Input
                  id="temperature-value"
                  type="number"
                  step="any"
                  min={floor}
                  value={rawValue}
                  onChange={(e) => setRawValue(e.target.value)}
                  className="font-mono"
                  aria-invalid={!!fieldError}
                  aria-describedby={fieldError ? 'temperature-value-error' : undefined}
                  data-testid="temperature-value-input"
                />
                {fieldError && (
                  <p id="temperature-value-error" className="flex items-center gap-1 text-xs text-destructive" data-testid="temperature-value-error" role="alert">
                    <AlertCircle className="h-3 w-3 shrink-0" /> {fieldError}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Input Unit
                </Label>
                <Select value={unit} onValueChange={setUnit}>
                  <SelectTrigger data-testid="temperature-unit-select" aria-label="Input unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.name} ({u.label})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 bg-muted/50 rounded-md">
            <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Display
            </h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Precision
                </Label>
                <Select value={String(precision)} onValueChange={(p) => setPrecision(Number(p))}>
                  <SelectTrigger data-testid="temperature-precision-select" aria-label="Display precision">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRECISION_OPTIONS.map((p) => (
                      <SelectItem key={p} value={String(p)}>{p} decimal place{p > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleCopy}
                  disabled={!formatted}
                  data-testid="temperature-copy-btn"
                >
                  <Copy className="h-4 w-4" /> Copy
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleReset}
                  data-testid="temperature-reset-btn"
                >
                  <RotateCcw className="h-4 w-4" /> Reset
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="lg:col-span-3 space-y-4">
        <Card className="technical-card h-fit">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Converted Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div role="status" aria-live="polite">
              {fieldError ? (
                <p className="text-sm text-muted-foreground" data-testid="temperature-results-placeholder">
                  Fix the highlighted field to see results.
                </p>
              ) : calcError ? (
                <div className="flex items-center gap-2 text-sm text-destructive" data-testid="temperature-calc-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
                </div>
              ) : !formatted ? (
                <p className="text-sm text-muted-foreground" data-testid="temperature-results-loading">
                  Calculating…
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="temperature-results">
                  {formatted.map((u) => (
                    <div
                      key={u.value}
                      className={`p-3 rounded-md border ${u.value === unit ? 'border-autoxium-blue bg-autoxium-blue/5' : 'border-structure bg-muted/30'}`}
                    >
                      <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                        {u.label} <span className="normal-case">— {u.name}</span>
                      </p>
                      <p className={`font-mono font-bold break-all ${u.value === unit ? 'text-lg text-autoxium-blue' : 'text-base'}`}>
                        {u.display}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="technical-card" data-testid="temperature-formula">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Formula &amp; Derivation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">method</Badge>
              Every value is first reduced to Celsius (°C), then expanded back out to all
              3 units. Temperature conversion is offset-based, not multiplicative — unlike
              this site's other unit converters.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">from °C</Badge>
              K = °C + 273.15 &nbsp;·&nbsp; °F = (°C × 9/5) + 32
            </div>
            {formatted && (
              <div className="pt-1 font-mono text-[11px] bg-muted/50 rounded-md px-3 py-2 break-all">
                {unitInfo.label} → °C = {results.celsius.toFixed(Math.max(precision, 4))} °C
              </div>
            )}
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">note</Badge>
              Values below absolute zero for the selected unit (-273.15°C / 0K / -459.67°F)
              are rejected — Celsius and Fahrenheit otherwise accept negative values freely.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TemperatureConverter;
